import { Link } from 'react-router-dom';
import { WineType, formatWineType, formatDistance } from '@wineryxplorer/shared';
import type { DistanceLocale } from '@wineryxplorer/shared';
import { WineRow } from '../WineRow';

export interface WineryGroupWine {
  _id: string;
  name: string;
  vintage?: number;
  varietal?: string;
  appellation?: string;
  price?: number;
  wineType?: WineType;
  venueId?: string | { _id: string; name: string } | null;
  proAvg?: number | null;
  proCount?: number;
  memberAvg?: number | null;
  memberCount?: number;
}

export interface WineryGroupSectionProps {
  winery: { _id: string; name: string; distanceMeters?: number };
  wines: WineryGroupWine[];
  distanceLocale: DistanceLocale;
}

const TYPE_ORDER: WineType[] = [
  WineType.RED,
  WineType.WHITE,
  WineType.ROSE,
  WineType.SPARKLING,
  WineType.DESSERT,
  WineType.FORTIFIED,
  WineType.OTHER,
];

export const WineryGroupSection = ({ winery, wines, distanceLocale }: WineryGroupSectionProps) => {
  const distanceLabel =
    typeof winery.distanceMeters === 'number'
      ? formatDistance(winery.distanceMeters, distanceLocale).display
      : null;

  // Bucket by wine type, sorted alphabetically within each type.
  const buckets = new Map<WineType | 'untyped', WineryGroupWine[]>();
  for (const w of wines) {
    const key = (w.wineType ?? 'untyped') as WineType | 'untyped';
    const arr = buckets.get(key) ?? [];
    arr.push(w);
    buckets.set(key, arr);
  }
  for (const arr of buckets.values()) {
    arr.sort((a, b) => a.name.localeCompare(b.name));
  }

  const orderedTypes: (WineType | 'untyped')[] = [
    ...TYPE_ORDER.filter((t) => buckets.has(t)),
    ...(buckets.has('untyped') ? (['untyped'] as const) : []),
  ];

  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between gap-3 px-4 py-3 bg-gray-100 border-b border-gray-200">
        <Link
          to={`/wineries/${winery._id}`}
          className="font-serif text-xl text-gray-900 hover:text-primary-700 truncate"
        >
          {winery.name}
        </Link>
        {distanceLabel && (
          <span className="text-sm text-gray-600 flex-shrink-0">{distanceLabel}</span>
        )}
      </div>
      {orderedTypes.map((type) => {
        const list = buckets.get(type) ?? [];
        return (
          <div key={type}>
            <div className="px-4 py-2 text-base font-bold text-gray-800 bg-gray-50 border-b border-gray-200">
              {type === 'untyped' ? 'Other wines' : formatWineType(type)}
            </div>
            {list.map((w) => (
              <WineRow key={w._id} wine={w} hideWineryName />
            ))}
          </div>
        );
      })}
    </section>
  );
};
